import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';

/**
 * Q&A pre-flight before opening a fresh Workflow canvas.
 *
 * A raw prompt like "modern house" gives the image model almost nothing
 * to hold on to. Three quick pills + one optional line closes the biggest
 * gaps (program, scale, context, style) so the first Concept the user
 * sees is coherent instead of generic. Everything is optional — the
 * Skip button preserves the current "just navigate with the prompt"
 * behaviour for power users.
 */

type Choice<T extends string> = { id: T; label: string };

const PROGRAMS: Choice<string>[] = [
  { id: 'Housing', label: 'Housing' },
  { id: 'Commercial', label: 'Commercial' },
  { id: 'Cultural', label: 'Cultural' },
  { id: 'Hospitality', label: 'Hospitality' },
  { id: 'Educational', label: 'Educational' },
  { id: 'Mixed-use', label: 'Mixed-use' },
];

const SCALES: Choice<string>[] = [
  { id: 'Small (< 500 m²)', label: 'Small' },
  { id: 'Medium (500–5,000 m²)', label: 'Medium' },
  { id: 'Large (> 5,000 m²)', label: 'Large' },
];

const CONTEXTS: Choice<string>[] = [
  { id: 'Urban', label: 'Urban' },
  { id: 'Suburban', label: 'Suburban' },
  { id: 'Rural', label: 'Rural' },
  { id: 'Coastal', label: 'Coastal' },
];

export interface PreflightAnswers {
  program?: string;
  scale?: string;
  context?: string;
  style?: string;
}

interface WorkflowPreflightModalProps {
  open: boolean;
  originalPrompt: string;
  onCancel: () => void;
  /** Called with the enriched prompt built from the answers. */
  onConfirm: (enrichedPrompt: string, answers: PreflightAnswers) => void;
  /** Skip — proceed with the untouched original prompt. */
  onSkip: () => void;
}

export default function WorkflowPreflightModal({
  open,
  originalPrompt,
  onCancel,
  onConfirm,
  onSkip,
}: WorkflowPreflightModalProps) {
  const [program, setProgram] = useState<string>('');
  const [scale, setScale] = useState<string>('');
  const [context, setContext] = useState<string>('');
  const [style, setStyle] = useState<string>('');

  // Reset each time the modal reopens so it doesn't remember answers
  // from a previous, unrelated workflow.
  useEffect(() => {
    if (open) {
      setProgram('');
      setScale('');
      setContext('');
      setStyle('');
    }
  }, [open]);

  const handleConfirm = () => {
    const parts: string[] = [originalPrompt.trim()];
    const answers: PreflightAnswers = {};
    if (program) { parts.push(`Program: ${program}`); answers.program = program; }
    if (scale) { parts.push(`Scale: ${scale}`); answers.scale = scale; }
    if (context) { parts.push(`Context: ${context}`); answers.context = context; }
    if (style.trim()) { parts.push(`Style direction: ${style.trim()}`); answers.style = style.trim(); }
    const enriched = parts.join('. ');
    onConfirm(enriched, answers);
  };

  // Radix Dialog handles: focus trap on open, focus restore on close,
  // Escape key, body scroll lock, aria-modal, aria-labelledby wiring,
  // and outside-click dismiss. Previously all hand-rolled — Tab would
  // escape the modal, and focus never returned to the trigger.
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-border bg-background font-display shadow-xl focus:outline-none">
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-primary">
              <SparklesIcon className="h-3.5 w-3.5" />
              Set the scene
            </div>
            <Dialog.Title className="text-lg font-semibold text-foreground">
              A few quick questions
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-muted-foreground">
              Orbi generates much more coherent designs when it knows the program,
              scale, and context. Skip any you don't have yet.
            </Dialog.Description>
          </div>
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          <div className="mb-5 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            <span className="mr-2 text-xs uppercase tracking-wide text-muted-foreground/70">Your prompt</span>
            <span className="text-foreground">{originalPrompt}</span>
          </div>

          <PillGroup label="Program" value={program} onChange={setProgram} options={PROGRAMS} />
          <PillGroup label="Scale" value={scale} onChange={setScale} options={SCALES} />
          <PillGroup label="Context" value={context} onChange={setContext} options={CONTEXTS} />

          <div className="mb-1">
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Style direction <span className="normal-case text-muted-foreground/60">— optional</span>
            </label>
            <input
              type="text"
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              placeholder="e.g. Scandinavian minimalism, brutalist, biophilic…"
              aria-label="Style direction"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/20 px-6 py-4">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Skip — use my prompt as-is
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            Start Workflow
          </button>
        </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PillGroup({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Choice<string>[];
}) {
  return (
    <div className="mb-5">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-[11px] text-muted-foreground/70 hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(active ? '' : opt.id)}
              aria-pressed={active}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:border-primary/60',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
